import { React, useEffect, useState } from 'react';
import ProgressBar from '../../components/progress-bar';
import '../../styling/listview.scss';
import { getPlaceDetails, getPlacePhoto } from '../../services/api';

function ListView({ itinerary, lodging, dining }) {
  const [currentDay, setCurrentDay] = useState(1);
  const [activeTab, setActiveTab] = useState('Trip');
  const [locations, setLocations] = useState([]);
  const [dinings, setDinings] = useState([]);
  const [lodgings, setLodgings] = useState([]);

  const handleDayClick = (day) => {
    setCurrentDay(day);
  };

  useEffect(() => {
    const fetchLocations = async () => {
      const itineraryEntries = itinerary ? Object.entries(itinerary) : [];
      const locationsPromises = itineraryEntries.flatMap(([day, placeIds]) => placeIds.map(async (placeId) => {
        const placeDetails = await getPlaceDetails(placeId);
        const placePhoto = await getPlacePhoto(placeId);
        return {
          placeName: placeDetails?.displayName?.text,
          placePhotoUrl: placePhoto,
          day, // Include day for reference
          placeAddress: placeDetails?.formattedAddress,
        };
      }));

      try {
        const loc = await Promise.all(locationsPromises);
        setLocations(loc); // Set locations in state
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };
    fetchLocations();
  }, [itinerary]);

  useEffect(() => {
    const fetchDiningOptions = async () => {
      const diningEntries = dining ? Object.entries(dining) : [];
      const diningOptionsPromises = diningEntries.flatMap(([day, placeIds]) => placeIds.map(async (placeId) => {
        const placeDetails = await getPlaceDetails(placeId); // Fetch details for each place ID
        const placePhoto = await getPlacePhoto(placeId);
        return {
          placeName: placeDetails?.displayName?.text,
          placePhotoUrl: placePhoto,
          day, // Include day for reference
          placeAddress: placeDetails?.formattedAddress,
        };
      }));

      try {
        const diningOptions = await Promise.all(diningOptionsPromises);
        setDinings(diningOptions); // Set locations in state
      } catch (error) {
        console.error('Error fetching dining options:', error);
      }
    };
    fetchDiningOptions();
  }, [dining]);

  useEffect(() => {
    const fetchLodgingOptions = async () => {
      const lodgingOptionsPromises = lodging.map(async (placeId) => {
        const placeDetails = await getPlaceDetails(placeId); // Fetch details for each lodging place ID
        const placePhoto = await getPlacePhoto(placeId);
        return {
          placeName: placeDetails?.displayName?.text,
          placePhotoUrl: placePhoto,
          placeAddress: placeDetails?.formattedAddress,
        };
      });

      try {
        const lodgingOptions = await Promise.all(lodgingOptionsPromises);
        setLodgings(lodgingOptions); // Set locations in state
      } catch (error) {
        console.error('Error fetching lodging options:', error);
      }
    };
    fetchLodgingOptions();
  }, [lodging]);

  return (
    <div className="list-view">
      <div className="toggle-space" />
      {/* Progress bar of days */}
      <div className="progress-bar">
        <ProgressBar
          currentDay={currentDay}
          totalDays={itinerary.length}
          onDayClick={handleDayClick}
        />
      </div>

      <div className="trip-section">
        {[...new Set([...locations.map((loc) => loc.day), ...dinings.map((loc) => loc.day), ...lodgings.map((lodge) => lodge.day)])]
          .map((day) => (
            currentDay === Number(day) + 1 && (
              <div key={day} className="day-container">
                <h2>Day {Number(day) + 1} Itinerary</h2>

                {/* store locations and dining */}
                <div className="locations">
                  {(() => {
                    // Extract locations & dining for this day
                    const dayLocations = locations.filter((loc) => loc.day === day);
                    const dayDinings = dinings.filter((din) => din.day === day);

                    // Determine if there are 3 dining options (for breakfast)
                    const hasBreakfast = dayDinings.length === 3;
                    const diningSlots = hasBreakfast
                      ? ['Breakfast', 'Lunch', 'Dinner']
                      : ['Lunch', 'Dinner'];

                    const orderedList = [];
                    let diningIndex = 0;

                    for (let i = 0; i < Math.max(dayLocations.length, dayDinings.length); i += 1) {
                      // Add Breakfast only if applicable
                      if (hasBreakfast && i === 0) {
                        orderedList.push({ ...dayDinings[0], timeSlot: 'Breakfast' });
                        diningIndex += 1;
                      }

                      // Add Location (Morning/Afternoon/Evening)
                      if (dayLocations[i]) {
                        orderedList.push({
                          ...dayLocations[i],
                          timeSlot: ['Morning', 'Afternoon', 'Evening'][i] || 'Evening',
                        });
                      }

                      if (diningIndex < dayDinings.length) {
                        orderedList.push({
                          ...dayDinings[diningIndex],
                          timeSlot: diningSlots[diningIndex] || 'Dining',
                        });
                        diningIndex += 1;
                      }
                    }

                    // layout
                    return orderedList.map((place, index) => (
                      <div key={index} className="location">
                        <span className="time">{place.timeSlot}</span>

                        <div className="timeline-details">
                          <div className="timeline">
                            <div
                              className={`circle ${
                                index === orderedList.length - 1 ? 'last-item' : ''
                              }`}
                            />
                          </div>

                          <div className="details">
                            <h3>{place.placeName || 'Loading...'}</h3>

                            <div className="image-des-container">
                              <div className="image-container">
                                {!place.placePhotoUrl && <p className="no-photo-text">Photo not available</p>}
                                <img
                                  src={place.placePhotoUrl || 'https://developers.elementor.com/docs/assets/img/elementor-placeholder-image.png'}
                                  alt={place.placeName || 'Placeholder'}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://developers.elementor.com/docs/assets/img/elementor-placeholder-image.png';
                                  }}
                                />
                              </div>
                              <p>{place.placeAddress || 'No address available.'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
                {/* end of stuff to change */}
              </div>
            )
          ))}
        {/* Lodging Section - Appears as a Frame at the Bottom */}
        {lodgings.length > 0 && (
          <div className="lodging-section">
            <h2>Lodging</h2>
            {lodgings.map((lodge, index) => (
              <div key={index} className="lodge-info">
                <div className="lodge-header">
                  <h3 className="lodge-name">{lodge.placeName}</h3>
                </div>

                <div className="lodge-content">
                  <div className="image-des-container">
                    <div className="image-container">
                      {!lodge.placePhotoUrl && <p className="no-photo-text">Photo not available</p>}
                      <img
                        src={lodge.placePhotoUrl || 'https://developers.elementor.com/docs/assets/img/elementor-placeholder-image.png'}
                        alt={lodge.placeName || 'Placeholder'}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://developers.elementor.com/docs/assets/img/elementor-placeholder-image.png';
                        }}
                      />
                    </div>
                  </div>
                  <p className="lodge-description">{lodge.placeAddress || 'No description available.'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ListView;
